import { useCallback, useEffect, useRef, useState } from 'react';
import { SEARCH_DEBOUNCE_MS } from '../../hooks/useRosterSearch';
import { useCrmViewParam } from '../../useCrmViewParam';

/** What the person typed, and the URL search it was typed against. */
interface TypedSearch {
  text: string;
  base: string;
}

type Timer = ReturnType<typeof setTimeout>;

function cancel(timer: { current: Timer | null }) {
  if (timer.current) clearTimeout(timer.current);
  timer.current = null;
}

/**
 * The Creators tab's search box. The URL-owned search is the one source of
 * truth: the box shows it, except while the person's own typing is pending
 * against the current URL. Typing updates the box at once and the URL one
 * pause later, so the server sees one request per pause rather than one per
 * keystroke. A URL change from elsewhere (back/forward, a link) no longer
 * matches what was typed, so the box shows the URL again with nothing to sync.
 */
export function useCrmSearchInput() {
  const { query, setQuery } = useCrmViewParam();
  const [typed, setTyped] = useState<TypedSearch | null>(null);
  const pause = useRef<Timer | null>(null);
  const input = typed?.base === query ? typed.text : query;

  useEffect(() => () => cancel(pause), []);

  const setInput = useCallback(
    (text: string) => {
      setTyped({ text, base: query });
      cancel(pause);
      pause.current = setTimeout(() => {
        // The URL is about to hold the trimmed text: keep showing what was typed (a trailing space) against it.
        setTyped({ text, base: text.trim() });
        setQuery(text);
      }, SEARCH_DEBOUNCE_MS);
    },
    [query, setQuery],
  );

  const clear = useCallback(() => {
    cancel(pause);
    setTyped(null);
    setQuery('');
  }, [setQuery]);

  return { input, setInput, query, clear };
}
